// Property create page — wires PropertyWizard to the create mutation
// and, when the user buffered files in step 3, uploads them in order
// after POST /properties returns the new id.
//
// Sequential upload is intentional: the backend assigns displayOrder
// as files arrive, so uploading in array order preserves the order
// the user dragged into.

import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { localizeError } from "@/lib/errorMessages";
import { uploadFileWithProgress } from "@/lib/upload-with-progress";
import { useAuthStore } from "@/lib/auth-store";
import { useCreateProperty } from "@/features/property/api";
import { PropertyWizard } from "@/features/property/wizard/property-wizard";
import type { PropertyWizardSubmitParams } from "@/features/property/wizard/property-wizard";
import { draftToCreateRequest } from "@/features/property/wizard/wizard-state";

export default function PropertyCreatePage() {
  const navigate = useNavigate();
  const mutation = useCreateProperty();
  // Separate state so the spinner stays on during image upload, not
  // just the property POST.
  const [uploadingImages, setUploadingImages] = useState(false);

  const handleSubmit = async ({ draft, pendingImages }: PropertyWizardSubmitParams) => {
    try {
      const created = await mutation.mutateAsync(draftToCreateRequest(draft));

      if (pendingImages.length > 0) {
        setUploadingImages(true);
        try {
          await uploadPendingImagesSequentially(created.id, pendingImages);
        } catch (uploadError) {
          // The property is created; some images failed. Don't reject
          // the whole flow — the user can retry uploads on the edit page.
          toast.warning(
            "Đã tạo BĐS nhưng một số ảnh tải lên chưa thành công. Bạn có thể thử lại từ trang chỉnh sửa.",
          );
          console.warn("Image upload after create failed:", uploadError);
        } finally {
          setUploadingImages(false);
        }
      }

      toast.success("Đã tạo bất động sản");
      navigate(`/properties/${created.id}`);
    } catch (error) {
      toast.error(localizeError(error));
      throw error;
    }
  };

  return (
    <PropertyWizard
      mode="create"
      onSubmit={handleSubmit}
      submitting={mutation.isPending || uploadingImages}
    />
  );
}

// ---------------------------------------------------------------------------
// Sequential image upload
// ---------------------------------------------------------------------------

/**
 * Upload buffered files one at a time so the backend assigns
 * {@code displayOrder} matching the user's drag-reordered sequence.
 * Parallel upload would race and could swap the primary image.
 */
async function uploadPendingImagesSequentially(
  propertyId: number,
  pendingImages: readonly { file: File }[],
): Promise<void> {
  const url = `/api/v1/properties/${propertyId}/images`;
  const token = useAuthStore.getState().accessToken;
  for (const { file } of pendingImages) {
    await uploadFileWithProgress(url, file, {
      field: "files",
      authToken: token,
    });
  }
}
